import React, { useCallback } from 'react';
import { usePathname, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { Navbar, NavbarBackLink, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useIsLiquidGlassEnabled } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ModalNavbarBackButton = () => {
  const router = useRouter();
  const currentPath = usePathname();

  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const { track } = useTelemetry();
  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldRenderModalNavbarBackButton = isNavbarV2Enabled && currentBreakpoint.isXS;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleBackPress = useCallback(() => {
    track('back_selected', { currentPath });

    if (router.canGoBack()) {
      router.back();

      return;
    }

    router.dismissTo('/');
  }, [currentPath, router, track]);

  // ───────────────────────────────────────────────────────────────────────────

  if (!shouldRenderModalNavbarBackButton) {
    return null;
  }

  return (
    <Navbar.Container isLiquidGlassEnabled={isLiquidGlassEnabled}>
      <Navbar.LinksGroup isLiquidGlassEnabled={isLiquidGlassEnabled}>
        <NavbarBackLink
          accessibilityLabel={formatMessage(messages.goBack)}
          onPress={handleBackPress}
        />
      </Navbar.LinksGroup>
    </Navbar.Container>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  goBack: {
    defaultMessage: 'Go back',
    description: 'Modal navbar | Go back',
  },
});
